import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ExampleIndustryDashboard from './ExampleIndustryDashboard';
import styles from './IndustryDashboard.module.css';
import asoStyles from './ASODashboard.module.css';
import { mapTilerKey } from '../Constants';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ComposedChart,
  ScatterChart,
  Scatter,
  Cell
} from 'recharts';
import * as XLSX from 'xlsx';
import { Download, Map as MapIcon, Filter, X } from 'lucide-react';

const IndustryDashboard = () => {
  const { user, userRole, logout } = useAuth();
  const [activeView, setActiveView] = useState('aso'); // 'aso' veya 'example'
  const [activeDashboard, setActiveDashboard] = useState('aso'); // 'aso', 'company', 'data', 'gis', 'drought', 'wastewater', 'footprint', 'ai', 'reports'
  const [anomalyIndicator, setAnomalyIndicator] = useState(true); // Anomali var mı?
  const [anomalyCount, setAnomalyCount] = useState(3); // Anomali sayısı

  // Eğer örnek dashboard'a geçilirse
  if (activeView === 'example') {
    return (
      <div>
        <div style={{ 
          position: 'fixed', 
          top: '10px', 
          right: '10px', 
          zIndex: 10000,
          padding: '10px',
          background: 'rgba(30, 41, 59, 0.9)',
          borderRadius: '8px',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <button 
            onClick={() => setActiveView('aso')}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            ASO Dashboard'a Dön
          </button>
        </div>
        <ExampleIndustryDashboard />
      </div>
    );
  }

  // ASO Dashboard içeriği
  return (
    <ASODashboard 
      activeDashboard={activeDashboard}
      setActiveDashboard={setActiveDashboard}
      anomalyIndicator={anomalyIndicator}
      anomalyCount={anomalyCount}
      onSwitchToExample={() => setActiveView('example')}
      logout={logout}
    />
  );
};

// ASO Dashboard Component
const ASODashboard = ({ 
  activeDashboard, 
  setActiveDashboard, 
  anomalyIndicator, 
  anomalyCount,
  onSwitchToExample,
  logout 
}) => {
  // Su hesabı verileri
  const [waterData, setWaterData] = useState({
    totalReceived: 1000000, // Toplam gelen su (L)
    totalUsed: 800000, // Kullanılan su (L)
    totalPaid: 1000000, // Askıya ödenen (L)
    difference: 200000, // Fark (patlak vs)
    companies: 350, // Toplam firma sayısı
    activeDevices: 320 // Aktif IoT cihaz sayısı
  });

  // Anomali tespit metodları (13 method)
  const anomalyMethods = [
    {
      id: 1,
      name: 'Z-Score (Standart Sapma)',
      description: 'Verilerin ortalamadan standart sapma cinsinden uzaklığını ölçer',
      status: 'active',
      detectedAnomalies: 12,
      threshold: '±2.5σ'
    },
    {
      id: 2,
      name: 'IQR (Interquartile Range)',
      description: 'Çeyrekler arası aralık kullanarak aykırı değerleri tespit eder',
      status: 'active',
      detectedAnomalies: 8,
      threshold: 'Q1-1.5*IQR, Q3+1.5*IQR'
    },
    {
      id: 3,
      name: 'Moving Average Deviation',
      description: 'Hareketli ortalama sapmasına göre anomali tespit eder',
      status: 'active',
      detectedAnomalies: 15,
      threshold: 'Dinamik'
    },
    {
      id: 4,
      name: 'Exponential Moving Average',
      description: 'Üssel ağırlıklı hareketli ortalama ile trend sapmalarını bulur',
      status: 'active',
      detectedAnomalies: 10,
      threshold: 'Dinamik'
    },
    {
      id: 5,
      name: 'Seasonal Decomposition',
      description: 'Mevsimsel desenleri ayırarak anomali tespit eder',
      status: 'active',
      detectedAnomalies: 5,
      threshold: 'Mevsimsel'
    },
    {
      id: 6,
      name: 'Isolation Forest',
      description: 'Makine öğrenmesi ile izole edilmiş veri noktalarını bulur',
      status: 'active',
      detectedAnomalies: 18,
      threshold: 'ML Model'
    },
    {
      id: 7,
      name: 'Local Outlier Factor (LOF)',
      description: 'Yerel yoğunluk bazlı anomali tespit yöntemi',
      status: 'active',
      detectedAnomalies: 14,
      threshold: 'Yoğunluk'
    },
    {
      id: 8,
      name: 'DBSCAN Clustering',
      description: 'Kümeleme algoritması ile aykırı noktaları tespit eder',
      status: 'active',
      detectedAnomalies: 9,
      threshold: 'Küme'
    },
    {
      id: 9,
      name: 'Statistical Process Control (SPC)',
      description: 'İstatistiksel süreç kontrolü ile kontrol limitlerini aşan değerleri bulur',
      status: 'active',
      detectedAnomalies: 7,
      threshold: 'UCL/LCL'
    },
    {
      id: 10,
      name: 'Percentile-based',
      description: 'Yüzdelik dilimlere göre aykırı değerleri tespit eder',
      status: 'active',
      detectedAnomalies: 11,
      threshold: 'P5, P95'
    },
    {
      id: 11,
      name: 'Median Absolute Deviation (MAD)',
      description: 'Medyan mutlak sapma kullanarak robust anomali tespiti',
      status: 'active',
      detectedAnomalies: 13,
      threshold: '3*MAD'
    },
    {
      id: 12,
      name: 'Time Series Decomposition',
      description: 'Zaman serisi bileşenlerini ayırarak anomali tespit eder',
      status: 'active',
      detectedAnomalies: 6,
      threshold: 'Trend+Seasonal'
    },
    {
      id: 13,
      name: 'Machine Learning Anomaly Detection',
      description: 'Derin öğrenme modelleri ile kompleks anomali desenlerini tespit eder',
      status: 'active',
      detectedAnomalies: 20,
      threshold: 'Neural Network'
    }
  ];

  // Anomali bildirimleri
  const [anomalyAlerts, setAnomalyAlerts] = useState([
    {
      id: 1,
      company: 'ABC Tekstil A.Ş.',
      type: 'Su Tüketimi Anomalisi',
      severity: 'high',
      method: 'Z-Score',
      value: 125000,
      expected: 85000,
      deviation: '+47%',
      timestamp: new Date(Date.now() - 3600000),
      notified: true
    },
    {
      id: 2,
      company: 'XYZ Gıda San.',
      type: 'Elektrik Tüketimi Anomalisi',
      severity: 'medium',
      method: 'IQR',
      value: 45000,
      expected: 38000,
      deviation: '+18%',
      timestamp: new Date(Date.now() - 7200000),
      notified: true
    },
    {
      id: 3,
      company: 'DEF Plastik Ltd.',
      type: 'Doğal Gaz Anomalisi',
      severity: 'high',
      method: 'Isolation Forest',
      value: 32000,
      expected: 25000,
      deviation: '+28%',
      timestamp: new Date(Date.now() - 1800000),
      notified: false
    }
  ]);

  // Anomali indikatorü yanıp sönme efekti
  useEffect(() => {
    if (!anomalyIndicator) return;
    const interval = setInterval(() => {
      // Burada gerçek zamanlı anomali kontrolü yapılabilir
    }, 2000);
    return () => clearInterval(interval);
  }, [anomalyIndicator]);

  // Su hesabı hesaplama
  const waterBalance = {
    totalReceived: waterData.totalReceived,
    totalUsed: waterData.totalUsed,
    totalPaid: waterData.totalPaid,
    difference: waterData.totalReceived - waterData.totalUsed,
    lossPercentage: ((waterData.totalReceived - waterData.totalUsed) / waterData.totalReceived) * 100
  };

    return (
    <div className={asoStyles.asoDashboard}>
      {/* Header */}
      <div className={asoStyles.header}>
        <div className={asoStyles.headerLeft}>
          <h1 className={asoStyles.title}>ASO 1. Organize Sanayi Bölgesi Başkanlığı</h1>
          <p className={asoStyles.subtitle}>IoT Cihaz Yönetim ve Anomali Tespit Sistemi</p>
        </div>
        <div className={asoStyles.headerRight}>
          {/* Anomali İndikatorü */}
          {anomalyIndicator && (
            <div className={asoStyles.anomalyIndicator}>
              <div className={asoStyles.anomalyDot}></div>
              <span className={asoStyles.anomalyCount}>{anomalyCount}</span>
              <span className={asoStyles.anomalyText}>Anomali</span>
            </div>
          )}
          <button 
            className={asoStyles.switchButton}
            onClick={onSwitchToExample}
            title="Örnek Dashboard'a Geç"
          >
            Örnek Dashboard
          </button>
          <button className={asoStyles.logoutButton} onClick={logout}>
            Çıkış Yap
          </button>
        </div>
      </div>

      {/* Dashboard Seçimi */}
      <div className={asoStyles.dashboardSelector}>
        <button
          className={`${asoStyles.selectorButton} ${activeDashboard === 'aso' ? asoStyles.active : ''}`}
          onClick={() => setActiveDashboard('aso')}
        >
          ASO Dashboard
        </button>
        <button
          className={`${asoStyles.selectorButton} ${activeDashboard === 'company' ? asoStyles.active : ''}`}
          onClick={() => setActiveDashboard('company')}
        >
          Fabrika ve Şirketler
        </button>
        <button
          className={`${asoStyles.selectorButton} ${activeDashboard === 'data' ? asoStyles.active : ''}`}
          onClick={() => setActiveDashboard('data')}
        >
          Veri Altyapısı
        </button>
        <button
          className={`${asoStyles.selectorButton} ${activeDashboard === 'gis' ? asoStyles.active : ''}`}
          onClick={() => setActiveDashboard('gis')}
        >
          CBS Entegrasyonu
        </button>
        <button
          className={`${asoStyles.selectorButton} ${activeDashboard === 'drought' ? asoStyles.active : ''}`}
          onClick={() => setActiveDashboard('drought')}
        >
          Kuraklık Senaryoları
        </button>
        <button
          className={`${asoStyles.selectorButton} ${activeDashboard === 'wastewater' ? asoStyles.active : ''}`}
          onClick={() => setActiveDashboard('wastewater')}
        >
          Atık Su & Arıtma
        </button>
        <button
          className={`${asoStyles.selectorButton} ${activeDashboard === 'footprint' ? asoStyles.active : ''}`}
          onClick={() => setActiveDashboard('footprint')}
        >
          Su Ayak İzi
        </button>
        <button
          className={`${asoStyles.selectorButton} ${activeDashboard === 'ai' ? asoStyles.active : ''}`}
          onClick={() => setActiveDashboard('ai')}
        >
          AI Analizler
        </button>
        <button
          className={`${asoStyles.selectorButton} ${activeDashboard === 'reports' ? asoStyles.active : ''}`}
          onClick={() => setActiveDashboard('reports')}
        >
          Raporlama
        </button>
      </div>
          
      {/* ASO Dashboard İçeriği */}
      {activeDashboard === 'aso' && (
        <div className={asoStyles.dashboardContent}>
          {/* Üst Metrikler */}
          <div className={asoStyles.metricsGrid}>
            <div className={asoStyles.metricCard}>
              <div className={asoStyles.metricIcon}></div>
              <div className={asoStyles.metricInfo}>
                <div className={asoStyles.metricLabel}>Toplam Firma</div>
                <div className={asoStyles.metricValue}>{waterData.companies}</div>
                <div className={asoStyles.metricChange}>Aktif: {waterData.activeDevices}</div>
              </div>
            </div>
            <div className={asoStyles.metricCard}>
              <div className={asoStyles.metricIcon}></div>
              <div className={asoStyles.metricInfo}>
                <div className={asoStyles.metricLabel}>IoT Cihazlar</div>
                <div className={asoStyles.metricValue}>{waterData.activeDevices}</div>
                <div className={asoStyles.metricChange}>Bağlı: {waterData.activeDevices}</div>
              </div>
            </div>
            <div className={asoStyles.metricCard}>
              <div className={asoStyles.metricIcon}></div>
              <div className={asoStyles.metricInfo}>
                <div className={asoStyles.metricLabel}>Aktif Anomaliler</div>
                <div className={asoStyles.metricValue} style={{ color: '#ef4444' }}>{anomalyCount}</div>
                <div className={asoStyles.metricChange}>Tespit Edildi</div>
              </div>
            </div>
            <div className={asoStyles.metricCard}>
              <div className={asoStyles.metricIcon}></div>
              <div className={asoStyles.metricInfo}>
                <div className={asoStyles.metricLabel}>Anomali Metodları</div>
                <div className={asoStyles.metricValue}>{anomalyMethods.length}</div>
                <div className={asoStyles.metricChange}>Aktif: {anomalyMethods.filter(m => m.status === 'active').length}</div>
              </div>
            </div>
          </div>

          {/* Su Hesabı Bölümü */}
          <div className={asoStyles.waterBalanceSection}>
            <h2 className={asoStyles.sectionTitle}>Su Hesabı Analizi</h2>
            <div className={asoStyles.waterBalanceGrid}>
              <div className={asoStyles.waterCard}>
                <div className={asoStyles.waterLabel}>Toplam Gelen Su</div>
                <div className={asoStyles.waterValue}>
                  {(waterBalance.totalReceived / 1000).toFixed(1)}k L
                </div>
              </div>
              <div className={asoStyles.waterCard}>
                <div className={asoStyles.waterLabel}>Kullanılan Su</div>
                <div className={asoStyles.waterValue}>
                  {(waterBalance.totalUsed / 1000).toFixed(1)}k L
                </div>
              </div>
              <div className={asoStyles.waterCard}>
                <div className={asoStyles.waterLabel}>Askıya Ödenen</div>
                <div className={asoStyles.waterValue}>
                  {(waterBalance.totalPaid / 1000).toFixed(1)}k L
                </div>
                      </div>
              <div className={`${asoStyles.waterCard} ${asoStyles.waterCardWarning}`}>
                <div className={asoStyles.waterLabel}>Fark (Patlak/Kayıp)</div>
                <div className={asoStyles.waterValue} style={{ color: '#ef4444' }}>
                  {(waterBalance.difference / 1000).toFixed(1)}k L
                      </div>
                <div className={asoStyles.waterPercentage}>
                  %{waterBalance.lossPercentage.toFixed(1)}
                    </div>
              </div>
            </div>
            {waterBalance.difference > 0 && (
              <div className={asoStyles.waterWarning}>
                {waterBalance.difference.toLocaleString('tr-TR')} L su kaybı tespit edildi. 
                Olası nedenler: Boru patlağı, sızıntı veya sayaç hatası.
              </div>
            )}
          </div>

          {/* Anomali Tespit Metodları */}
          <div className={asoStyles.methodsSection}>
            <h2 className={asoStyles.sectionTitle}>Anomali Tespit Metodları (13 Method)</h2>
            <div className={asoStyles.methodsGrid}>
              {anomalyMethods.map((method) => (
                <div key={method.id} className={asoStyles.methodCard}>
                  <div className={asoStyles.methodHeader}>
                    <div className={asoStyles.methodNumber}>{method.id}</div>
                    <div className={asoStyles.methodStatus} data-status={method.status}></div>
        </div>
                  <h3 className={asoStyles.methodName}>{method.name}</h3>
                  <p className={asoStyles.methodDescription}>{method.description}</p>
                  <div className={asoStyles.methodStats}>
                    <div className={asoStyles.methodStat}>
                      <span className={asoStyles.methodStatLabel}>Tespit:</span>
                      <span className={asoStyles.methodStatValue}>{method.detectedAnomalies}</span>
      </div>
                    <div className={asoStyles.methodStat}>
                      <span className={asoStyles.methodStatLabel}>Eşik:</span>
                      <span className={asoStyles.methodStatValue}>{method.threshold}</span>
        </div>
        </div>
      </div>
              ))}
        </div>
          </div>

          {/* Anomali Bildirimleri */}
          <div className={asoStyles.alertsSection}>
            <h2 className={asoStyles.sectionTitle}>Anomali Bildirimleri</h2>
            <div className={asoStyles.alertsList}>
              {anomalyAlerts.map((alert) => (
                <div key={alert.id} className={`${asoStyles.alertCard} ${asoStyles[`alert${alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}`]}`}>
                  <div className={asoStyles.alertHeader}>
                    <div className={asoStyles.alertCompany}>{alert.company}</div>
                    <div className={asoStyles.alertSeverity} data-severity={alert.severity}>
                      {alert.severity === 'high' ? 'Yüksek' : alert.severity === 'medium' ? 'Orta' : 'Düşük'}
                    </div>
                  </div>
                  <div className={asoStyles.alertType}>{alert.type}</div>
                  <div className={asoStyles.alertDetails}>
                    <div className={asoStyles.alertDetail}>
                      <span>Metod:</span> {alert.method}
                      </div>
                    <div className={asoStyles.alertDetail}>
                      <span>Değer:</span> {alert.value.toLocaleString('tr-TR')} L
                    </div>
                    <div className={asoStyles.alertDetail}>
                      <span>Beklenen:</span> {alert.expected.toLocaleString('tr-TR')} L
                      </div>
                    <div className={asoStyles.alertDetail}>
                      <span>Sapma:</span> <strong style={{ color: '#ef4444' }}>{alert.deviation}</strong>
                    </div>
                  </div>
                  <div className={asoStyles.alertFooter}>
                    <div className={asoStyles.alertTime}>
                      {alert.timestamp.toLocaleString('tr-TR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                    <div className={asoStyles.alertNotification}>
                      {alert.notified ? 'Mail Gönderildi' : 'Mail Gönderilecek'}
                  </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Fabrika ve Şirketler Dashboard */}
      {activeDashboard === 'company' && (
        <CompanyDashboard />
      )}

      {/* İş Paketi 1: Veri Altyapısı Dashboard */}
      {activeDashboard === 'data' && (
        <DataInfrastructureDashboard />
      )}

      {/* İş Paketi 2: CBS Entegrasyonu Dashboard */}
      {activeDashboard === 'gis' && (
        <GISIntegrationDashboard />
      )}

      {/* İş Paketi 3: Kuraklık Senaryoları Dashboard */}
      {activeDashboard === 'drought' && (
        <DroughtScenariosDashboard />
      )}

      {/* İş Paketi 4: Atık Su & Arıtma Dashboard */}
      {activeDashboard === 'wastewater' && (
        <WastewaterTreatmentDashboard />
      )}

      {/* İş Paketi 5: Su Ayak İzi Dashboard */}
      {activeDashboard === 'footprint' && (
        <WaterFootprintDashboard />
      )}

      {/* İş Paketi 6: AI Analizler Dashboard */}
      {activeDashboard === 'ai' && (
        <AIAnalyticsDashboard />
      )}

      {/* İş Paketi 7: Raporlama Dashboard */}
      {activeDashboard === 'reports' && (
        <ReportingDashboard />
      )}
    </div>
  );
};

// İş Paketi 1: Veri Altyapısı Dashboard
const DataInfrastructureDashboard = () => {
  const [companies] = useState(() => {
    const sectors = ['Tekstil', 'Gıda', 'Plastik', 'Metal', 'Kimya', 'Otomotiv', 'Kağıt', 'Elektronik'];
    const companies = [];
    for (let i = 1; i <= 350; i++) {
      const sector = sectors[Math.floor(Math.random() * sectors.length)];
      companies.push({
        id: i,
        name: `${sector} Şirketi ${i}`,
        sector,
        annualWaterConsumption: Math.floor(Math.random() * 500000) + 50000, // m³/yıl
        wastewaterLoad: Math.floor(Math.random() * 400000) + 40000, // m³/yıl
        area: Math.floor(Math.random() * 50000) + 5000, // m²
        employees: Math.floor(Math.random() * 200) + 10
      });
    }
    return companies;
  });

  const sectorDistribution = companies.reduce((acc, company) => {
    if (!acc[company.sector]) {
      acc[company.sector] = { count: 0, totalWater: 0, totalWastewater: 0, totalArea: 0 };
    }
    acc[company.sector].count++;
    acc[company.sector].totalWater += company.annualWaterConsumption;
    acc[company.sector].totalWastewater += company.wastewaterLoad;
    acc[company.sector].totalArea += company.area;
    return acc;
  }, {});

  const sectorData = Object.entries(sectorDistribution).map(([sector, data]) => ({
    sector,
    companies: data.count,
    waterConsumption: (data.totalWater / 1000).toFixed(1),
    wastewater: (data.totalWastewater / 1000).toFixed(1),
    area: (data.totalArea / 1000).toFixed(1)
  }));

  const totalWater = companies.reduce((sum, c) => sum + c.annualWaterConsumption, 0);
  const totalWastewater = companies.reduce((sum, c) => sum + c.wastewaterLoad, 0);
  const totalArea = companies.reduce((sum, c) => sum + c.area, 0);

  return (
    <div className={asoStyles.dashboardContent}>
      <div className={asoStyles.companyDashboard}>
        <h2 className={asoStyles.sectionTitle}>OSB Veri Altyapısı ve Veritabanı</h2>
        <p className={asoStyles.companyDescription}>
          OSB ölçeğinde su yönetimi, kuraklık ve atık su analizlerine temel olacak standartlaştırılmış veri altyapısı
        </p>

        {/* Özet Metrikler */}
        <div className={asoStyles.metricsGrid}>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Toplam İşletme</div>
              <div className={asoStyles.metricValue}>{companies.length}</div>
            </div>
          </div>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Toplam Yıllık Su (m³/yıl)</div>
              <div className={asoStyles.metricValue}>{(totalWater / 1000000).toFixed(2)}M</div>
            </div>
          </div>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Toplam Atık Su (m³/yıl)</div>
              <div className={asoStyles.metricValue}>{(totalWastewater / 1000000).toFixed(2)}M</div>
            </div>
          </div>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Toplam Alan (m²)</div>
              <div className={asoStyles.metricValue}>{(totalArea / 1000000).toFixed(2)}M</div>
            </div>
          </div>
        </div>

        {/* Sektör Dağılımı */}
        <div className={asoStyles.chartCard}>
          <h3 className={asoStyles.chartTitle}>Sektör Dağılımı ve Su Profilleri</h3>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={sectorData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="sector" angle={-45} textAnchor="end" height={100} />
              <YAxis yAxisId="left" label={{ value: 'İşletme Sayısı', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="right" orientation="right" label={{ value: 'Su/Atık Su (Bin m³)', angle: 90, position: 'insideRight' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                  border: '1px solid rgba(148, 163, 184, 0.5)',
                  color: '#1e293b',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                labelStyle={{ color: '#1e293b', fontWeight: '600' }}
                itemStyle={{ color: '#1e293b' }}
              />
              <Legend wrapperStyle={{ color: '#1e293b' }} />
              <Bar yAxisId="left" dataKey="companies" fill="#3b82f6" name="İşletme Sayısı" />
              <Line yAxisId="right" type="monotone" dataKey="waterConsumption" stroke="#10b981" strokeWidth={2} name="Su Tüketimi (Bin m³)" />
              <Line yAxisId="right" type="monotone" dataKey="wastewater" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" name="Atık Su (Bin m³)" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* İşletme Bazlı Su Profilleri Tablosu */}
        <div className={asoStyles.companiesTableSection}>
          <h3 className={asoStyles.sectionTitle}>İşletme Bazlı Su Profilleri</h3>
          <div className={asoStyles.tableWrapper}>
            <table className={asoStyles.companiesTable}>
              <thead>
                <tr>
                  <th>İşletme Adı</th>
                  <th>Sektör</th>
                  <th>Yıllık Su Tüketimi (m³/yıl)</th>
                  <th>Atık Su Yükü (m³/yıl)</th>
                  <th>Alan (m²)</th>
                  <th>Çalışan</th>
                </tr>
              </thead>
              <tbody>
                {companies.slice(0, 50).map(company => (
                  <tr key={company.id}>
                    <td><strong>{company.name}</strong></td>
                    <td>{company.sector}</td>
                    <td>{(company.annualWaterConsumption / 1000).toFixed(1)}k</td>
                    <td>{(company.wastewaterLoad / 1000).toFixed(1)}k</td>
                    <td>{(company.area / 1000).toFixed(1)}k</td>
                    <td>{company.employees}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// İş Paketi 2: CBS Entegrasyonu Dashboard
const GISIntegrationDashboard = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [showMap, setShowMap] = useState(true);
  const [showWaterPoints, setShowWaterPoints] = useState(true);
  const [showTreatmentPlants, setShowTreatmentPlants] = useState(true);

  const waterPoints = [
    { id: 1, name: 'Su Giriş Noktası 1', type: 'entry', lat: 39.9334, lng: 32.8597, flow: 50000 },
    { id: 2, name: 'Su Giriş Noktası 2', type: 'entry', lat: 39.9400, lng: 32.8650, flow: 30000 },
    { id: 3, name: 'Su Çıkış Noktası 1', type: 'exit', lat: 39.9200, lng: 32.8500, flow: 45000 },
    { id: 4, name: 'Su Çıkış Noktası 2', type: 'exit', lat: 39.9250, lng: 32.8550, flow: 35000 }
  ];

  const treatmentPlants = [
    { id: 1, name: 'Arıtma Tesisi 1', lat: 39.9300, lng: 32.8600, capacity: 100000, currentLoad: 75000, type: 'biological' },
    { id: 2, name: 'Arıtma Tesisi 2', lat: 39.9350, lng: 32.8620, capacity: 80000, currentLoad: 60000, type: 'chemical' }
  ];

  useEffect(() => {
    if (!showMap || !mapContainer.current || map.current) return;

    const maplibregl = window.maplibregl;
    if (!maplibregl) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: 'https://api.maptiler.com/maps/basic-v2/style.json?key=' + mapTilerKey,
      center: [32.8597, 39.9334],
      zoom: 13
    });

    map.current.on('load', () => {
      // Su giriş-çıkış noktaları
      if (showWaterPoints) {
        const waterSource = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: waterPoints.map(point => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [point.lng, point.lat] },
              properties: { name: point.name, type: point.type, flow: point.flow }
            }))
          }
        };
        map.current.addSource('water-points', waterSource);
        map.current.addLayer({
          id: 'water-points-layer',
          type: 'circle',
          source: 'water-points',
          paint: {
            'circle-radius': 10,
            'circle-color': ['case',
              ['==', ['get', 'type'], 'entry'], '#10b981',
              '#3b82f6'
            ],
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
          }
        });
      }

      // Arıtma tesisleri
      if (showTreatmentPlants) {
        const treatmentSource = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: treatmentPlants.map(plant => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [plant.lng, plant.lat] },
              properties: { name: plant.name, capacity: plant.capacity, currentLoad: plant.currentLoad, type: plant.type }
            }))
          }
        };
        map.current.addSource('treatment-plants', treatmentSource);
        map.current.addLayer({
          id: 'treatment-plants-layer',
          type: 'circle',
          source: 'treatment-plants',
          paint: {
            'circle-radius': 15,
            'circle-color': '#f59e0b',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
          }
        });
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [showMap, showWaterPoints, showTreatmentPlants]);

  return (
    <div className={asoStyles.dashboardContent}>
      <div className={asoStyles.companyDashboard}>
        <h2 className={asoStyles.sectionTitle}>CBS ve Mekânsal Entegrasyon</h2>
        <p className={asoStyles.companyDescription}>
          OSB içindeki su ve atık su akışlarının mekânsal olarak izlenebilir ve analiz edilebilir hale getirilmesi
        </p>

        <div className={asoStyles.mapControls}>
          <button
            className={`${asoStyles.mapButton} ${showWaterPoints ? asoStyles.active : ''}`}
            onClick={() => setShowWaterPoints(!showWaterPoints)}
          >
            Su Giriş-Çıkış Noktaları
          </button>
          <button
            className={`${asoStyles.mapButton} ${showTreatmentPlants ? asoStyles.active : ''}`}
            onClick={() => setShowTreatmentPlants(!showTreatmentPlants)}
          >
            Arıtma Tesisleri
          </button>
        </div>

        {showMap && (
          <div className={asoStyles.mapContainer}>
            <div ref={mapContainer} className={asoStyles.map} />
            <div className={asoStyles.mapLegend}>
              <h4>Harita Göstergesi</h4>
              <div className={asoStyles.legendItems}>
                <div className={asoStyles.legendItem}>
                  <div className={asoStyles.legendColor} style={{ background: '#10b981' }}></div>
                  <span>Su Giriş Noktası</span>
                </div>
                <div className={asoStyles.legendItem}>
                  <div className={asoStyles.legendColor} style={{ background: '#3b82f6' }}></div>
                  <span>Su Çıkış Noktası</span>
                </div>
                <div className={asoStyles.legendItem}>
                  <div className={asoStyles.legendColor} style={{ background: '#f59e0b' }}></div>
                  <span>Arıtma Tesisi</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Su Giriş-Çıkış Noktaları Tablosu */}
        <div className={asoStyles.chartCard}>
          <h3 className={asoStyles.chartTitle}>Su Giriş-Çıkış Noktaları</h3>
          <div className={asoStyles.tableWrapper}>
            <table className={asoStyles.companiesTable}>
              <thead>
                <tr>
                  <th>Nokta Adı</th>
                  <th>Tip</th>
                  <th>Debi (m³/gün)</th>
                  <th>Koordinat</th>
                </tr>
              </thead>
              <tbody>
                {waterPoints.map(point => (
                  <tr key={point.id}>
                    <td><strong>{point.name}</strong></td>
                    <td>{point.type === 'entry' ? 'Giriş' : 'Çıkış'}</td>
                    <td>{point.flow.toLocaleString('tr-TR')}</td>
                    <td>{point.lat.toFixed(4)}, {point.lng.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Arıtma Tesisleri Tablosu */}
        <div className={asoStyles.chartCard}>
          <h3 className={asoStyles.chartTitle}>Arıtma Tesisleri</h3>
          <div className={asoStyles.tableWrapper}>
            <table className={asoStyles.companiesTable}>
              <thead>
                <tr>
                  <th>Tesis Adı</th>
                  <th>Kapasite (m³/gün)</th>
                  <th>Mevcut Yük (m³/gün)</th>
                  <th>Kullanım Oranı</th>
                  <th>Tip</th>
                </tr>
              </thead>
              <tbody>
                {treatmentPlants.map(plant => (
                  <tr key={plant.id}>
                    <td><strong>{plant.name}</strong></td>
                    <td>{plant.capacity.toLocaleString('tr-TR')}</td>
                    <td>{plant.currentLoad.toLocaleString('tr-TR')}</td>
                    <td>{((plant.currentLoad / plant.capacity) * 100).toFixed(1)}%</td>
                    <td>{plant.type === 'biological' ? 'Biyolojik' : 'Kimyasal'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// İş Paketi 3: Kuraklık Senaryoları Dashboard
const DroughtScenariosDashboard = () => {
  const [selectedScenario, setSelectedScenario] = useState('moderate');
  
  const scenarios = {
    moderate: { name: 'Orta Kuraklık', waterReduction: 20, duration: '3 ay' },
    severe: { name: 'Şiddetli Kuraklık', waterReduction: 40, duration: '6 ay' },
    extreme: { name: 'Aşırı Kuraklık', waterReduction: 60, duration: '12 ay' }
  };

  const baseWaterSupply = 1000000; // m³/yıl
  const currentDemand = 850000; // m³/yıl
  const scenario = scenarios[selectedScenario];
  const reducedSupply = baseWaterSupply * (1 - scenario.waterReduction / 100);
  const deficit = currentDemand - reducedSupply;
  const deficitPercentage = (deficit / currentDemand) * 100;

  const prioritySectors = [
    { sector: 'Gıda', priority: 1, critical: true, waterNeeded: 120000 },
    { sector: 'Kimya', priority: 2, critical: true, waterNeeded: 150000 },
    { sector: 'Tekstil', priority: 3, critical: false, waterNeeded: 180000 },
    { sector: 'Metal', priority: 4, critical: false, waterNeeded: 100000 },
    { sector: 'Plastik', priority: 5, critical: false, waterNeeded: 80000 }
  ];

  const alternativeSources = [
    { source: 'Geri Kazanım', potential: 200000, cost: 5000000, feasibility: 'Yüksek' },
    { source: 'Yeniden Kullanım', potential: 150000, cost: 3000000, feasibility: 'Orta' },
    { source: 'Yağmur Suyu Toplama', potential: 50000, cost: 2000000, feasibility: 'Düşük' }
  ];

  return (
    <div className={asoStyles.dashboardContent}>
      <div className={asoStyles.companyDashboard}>
        <h2 className={asoStyles.sectionTitle}>Kuraklık ve Su Krizi Senaryoları</h2>
        <p className={asoStyles.companyDescription}>
          OSB'lerin kuraklık koşullarında üretim sürekliliğini koruyabilmesi için senaryo bazlı karar destek mekanizmaları
        </p>

        {/* Senaryo Seçimi */}
        <div className={asoStyles.scenarioSelector}>
          <h3>Kuraklık Senaryosu Seçiniz:</h3>
          <div className={asoStyles.scenarioButtons}>
            {Object.entries(scenarios).map(([key, value]) => (
              <button
                key={key}
                className={`${asoStyles.scenarioButton} ${selectedScenario === key ? asoStyles.active : ''}`}
                onClick={() => setSelectedScenario(key)}
              >
                {value.name}
                <span>Su Azalması: %{value.waterReduction}</span>
                <span>Süre: {value.duration}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Senaryo Analizi */}
        <div className={asoStyles.metricsGrid}>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Mevcut Su Arzı</div>
              <div className={asoStyles.metricValue}>{(baseWaterSupply / 1000).toFixed(0)}k m³</div>
            </div>
          </div>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Azaltılmış Su Arzı</div>
              <div className={asoStyles.metricValue} style={{ color: '#ef4444' }}>
                {(reducedSupply / 1000).toFixed(0)}k m³
              </div>
            </div>
          </div>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Su Açığı</div>
              <div className={asoStyles.metricValue} style={{ color: '#ef4444' }}>
                {(deficit / 1000).toFixed(0)}k m³
              </div>
              <div className={asoStyles.metricChange}>%{deficitPercentage.toFixed(1)}</div>
            </div>
          </div>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Mevcut Talep</div>
              <div className={asoStyles.metricValue}>{(currentDemand / 1000).toFixed(0)}k m³</div>
            </div>
          </div>
        </div>

        {/* Sektör Önceliklendirme */}
        <div className={asoStyles.chartCard}>
          <h3 className={asoStyles.chartTitle}>Sektör Bazlı Önceliklendirme</h3>
          <div className={asoStyles.tableWrapper}>
            <table className={asoStyles.companiesTable}>
              <thead>
                <tr>
                  <th>Öncelik</th>
                  <th>Sektör</th>
                  <th>Kritik</th>
                  <th>İhtiyaç (m³/yıl)</th>
                  <th>Önerilen Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {prioritySectors.map((item, idx) => (
                  <tr key={idx}>
                    <td><strong>#{item.priority}</strong></td>
                    <td>{item.sector}</td>
                    <td>
                      <span className={`${asoStyles.statusBadge} ${item.critical ? asoStyles.statusWarning : asoStyles.statusActive}`}>
                        {item.critical ? 'Kritik' : 'Normal'}
                      </span>
                    </td>
                    <td>{(item.waterNeeded / 1000).toFixed(0)}k</td>
                    <td>{item.critical ? 'Öncelikli Su Temini' : 'Kısıtlı Su Temini'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Alternatif Su Kaynakları */}
        <div className={asoStyles.chartCard}>
          <h3 className={asoStyles.chartTitle}>Alternatif Su Kaynakları Senaryoları</h3>
          <div className={asoStyles.tableWrapper}>
            <table className={asoStyles.companiesTable}>
              <thead>
                <tr>
                  <th>Kaynak</th>
                  <th>Potansiyel (m³/yıl)</th>
                  <th>Tahmini Maliyet (TL)</th>
                  <th>Fizibilite</th>
                </tr>
              </thead>
              <tbody>
                {alternativeSources.map((source, idx) => (
                  <tr key={idx}>
                    <td><strong>{source.source}</strong></td>
                    <td>{(source.potential / 1000).toFixed(0)}k</td>
                    <td>{(source.cost / 1000000).toFixed(2)}M</td>
                    <td>
                      <span className={`${asoStyles.statusBadge} ${
                        source.feasibility === 'Yüksek' ? asoStyles.statusActive : 
                        source.feasibility === 'Orta' ? asoStyles.statusWarning : 
                        asoStyles.statusWarning
                      }`}>
                        {source.feasibility}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// İş Paketi 4: Atık Su & Arıtma Dashboard
const WastewaterTreatmentDashboard = () => {
  const [companies] = useState(() => {
    const sectors = ['Tekstil', 'Gıda', 'Plastik', 'Metal', 'Kimya'];
    return Array.from({ length: 50 }, (_, i) => ({
      id: i + 1,
      name: `${sectors[i % sectors.length]} Şirketi ${i + 1}`,
      sector: sectors[i % sectors.length],
      wastewaterLoad: Math.floor(Math.random() * 100000) + 10000,
      wastewaterType: ['Organik', 'Kimyasal', 'Karışık'][Math.floor(Math.random() * 3)],
      treatmentNeeded: Math.random() > 0.3
    }));
  });

  const sectorWastewater = companies.reduce((acc, company) => {
    if (!acc[company.sector]) {
      acc[company.sector] = { total: 0, count: 0, needsTreatment: 0 };
    }
    acc[company.sector].total += company.wastewaterLoad;
    acc[company.sector].count++;
    if (company.treatmentNeeded) acc[company.sector].needsTreatment++;
    return acc;
  }, {});

  const sectorData = Object.entries(sectorWastewater).map(([sector, data]) => ({
    sector,
    totalWastewater: (data.total / 1000).toFixed(1),
    companies: data.count,
    needsTreatment: data.needsTreatment
  }));

  const currentCapacity = 180000; // m³/gün
  const totalWastewater = companies.reduce((sum, c) => sum + c.wastewaterLoad, 0);
  const dailyWastewater = totalWastewater / 365;
  const capacityUtilization = (dailyWastewater / currentCapacity) * 100;

  return (
    <div className={asoStyles.dashboardContent}>
      <div className={asoStyles.companyDashboard}>
        <h2 className={asoStyles.sectionTitle}>Atık Su, Kirli Su ve Arıtma Tesisi Fizibilite</h2>
        <p className={asoStyles.companyDescription}>
          OSB'lerde atıksu yüklerinin sayısallaştırılması ve arıtma / geri kazanım yatırımlarının veri temelli planlanması
        </p>

        {/* Kapasite Analizi */}
        <div className={asoStyles.metricsGrid}>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Toplam Atık Su (m³/yıl)</div>
              <div className={asoStyles.metricValue}>{(totalWastewater / 1000000).toFixed(2)}M</div>
            </div>
          </div>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Mevcut Kapasite (m³/gün)</div>
              <div className={asoStyles.metricValue}>{(currentCapacity / 1000).toFixed(0)}k</div>
            </div>
          </div>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Kapasite Kullanımı</div>
              <div className={asoStyles.metricValue} style={{ color: capacityUtilization > 80 ? '#ef4444' : '#10b981' }}>
                %{capacityUtilization.toFixed(1)}
              </div>
            </div>
          </div>
          <div className={asoStyles.metricCard}>
            <div className={asoStyles.metricIcon}></div>
            <div className={asoStyles.metricInfo}>
              <div className={asoStyles.metricLabel}>Arıtma Gereken İşletme</div>
              <div className={asoStyles.metricValue}>
                {companies.filter(c => c.treatmentNeeded).length}
              </div>
            </div>
          </div>
        </div>

        {/* Sektör Bazlı Atık Su Analizi */}
        <div className={asoStyles.chartCard}>
          <h3 className={asoStyles.chartTitle}>Sektör Bazlı Kirli Su Yükü Analizi</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={sectorData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="sector" 
                tick={{ fill: '#1e293b', fontSize: 12 }}
              />
              <YAxis 
                label={{ value: 'Atık Su (Bin m³/yıl)', angle: -90, position: 'insideLeft' }}
                tick={{ fill: '#1e293b', fontSize: 12 }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                  border: '1px solid rgba(148, 163, 184, 0.5)',
                  color: '#1e293b',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                labelStyle={{ color: '#1e293b', fontWeight: '600' }}
                itemStyle={{ color: '#1e293b' }}
              />
              <Legend wrapperStyle={{ color: '#1e293b' }} />
              <Bar dataKey="totalWastewater" fill="#ef4444" name="Toplam Atık Su (Bin m³)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* İşletme Bazlı Atık Su Tablosu */}
        <div className={asoStyles.companiesTableSection}>
          <h3 className={asoStyles.sectionTitle}>İşletme Bazlı Atık Su Yükleri</h3>
          <div className={asoStyles.tableWrapper}>
            <table className={asoStyles.companiesTable}>
              <thead>
                <tr>
                  <th>İşletme Adı</th>
                  <th>Sektör</th>
                  <th>Atık Su Yükü (m³/yıl)</th>
                  <th>Atık Su Tipi</th>
                  <th>Arıtma Gereksinimi</th>
                </tr>
              </thead>
              <tbody>
                {companies.slice(0, 30).map(company => (
                  <tr key={company.id}>
                    <td><strong>{company.name}</strong></td>
                    <td>{company.sector}</td>
                    <td>{(company.wastewaterLoad / 1000).toFixed(1)}k</td>
                    <td>{company.wastewaterType}</td>
                    <td>
                      <span className={`${asoStyles.statusBadge} ${company.treatmentNeeded ? asoStyles.statusWarning : asoStyles.statusActive}`}>
                        {company.treatmentNeeded ? 'Gerekli' : 'Gerekli Değil'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// İş Paketi 5: Su Ayak İzi Dashboard
const WaterFootprintDashboard = () => {
  const [selectedSector, setSelectedSector] = useState('all');
  
  const sectors = ['Tekstil', 'Gıda', 'Plastik', 'Metal', 'Kimya'];
  const [companies] = useState(() => {
    // Sektör bazlı gerçekçi su ayak izi değerleri
    const sectorFootprints = {
      'Tekstil': { base: 65, range: 30 }, // 35-95 L/birim
      'Gıda': { base: 85, range: 60 }, // 25-145 L/birim
      'Plastik': { base: 35, range: 30 }, // 5-65 L/birim
      'Metal': { base: 45, range: 40 }, // 5-85 L/birim
      'Kimya': { base: 105, range: 70 } // 35-175 L/birim
    };
    
    return Array.from({ length: 100 }, (_, i) => {
      const sector = sectors[i % sectors.length];
      const footprintData = sectorFootprints[sector] || { base: 50, range: 40 };
      const production = Math.floor(Math.random() * 15000) + 2000; // 2k-17k birim ürün
      const footprint = footprintData.base + (Math.random() - 0.5) * footprintData.range;
      const totalWater = production * footprint;
      const wastewaterPerUnit = footprint * 0.7; // %70 atık su
      
      return {
        id: i + 1,
        name: `${sector} Şirketi ${i + 1}`,
        sector,
        production,
        waterPerUnit: footprint,
        totalWater,
        wastewaterPerUnit,
        footprint: parseFloat(footprint.toFixed(2)),
        reusePotential: Math.floor(Math.random() * 20) + 15 // %15-35 geri kazanım potansiyeli
      };
    });
  });

  const filteredCompanies = selectedSector === 'all' 
    ? companies 
    : companies.filter(c => c.sector === selectedSector);

  // Sektör seçimine göre grafik verilerini filtrele
  const sectorFootprints = selectedSector === 'all'
    ? sectors.map(sector => {
        const sectorCompanies = companies.filter(c => c.sector === sector);
        const avgFootprint = sectorCompanies.reduce((sum, c) => sum + c.footprint, 0) / sectorCompanies.length;
        const totalProduction = sectorCompanies.reduce((sum, c) => sum + c.production, 0);
        const totalWater = sectorCompanies.reduce((sum, c) => sum + c.totalWater, 0);
        
        return {
          sector,
          avgFootprint: avgFootprint.toFixed(2),
          totalProduction,
          totalWater: (totalWater / 1000).toFixed(1),
          companies: sectorCompanies.length
        };
      })
    : [{
        sector: selectedSector,
        avgFootprint: (filteredCompanies.reduce((sum, c) => sum + c.footprint, 0) / filteredCompanies.length).toFixed(2),
        totalProduction: filteredCompanies.reduce((sum, c) => sum + c.production, 0),
        totalWater: (filteredCompanies.reduce((sum, c) => sum + c.totalWater, 0) / 1000).toFixed(1),
        companies: filteredCompanies.length
      }];

  return (
    <div className={asoStyles.dashboardContent}>
      <div className={asoStyles.companyDashboard}>
        <h2 className={asoStyles.sectionTitle}>Su Ayak İzi Analizi</h2>
        <p className={asoStyles.companyDescription}>
          Sektörel ve işletme bazlı su ayak izi hesaplamaları ve geri kazanım potansiyeli değerlendirmesi
        </p>

        {/* Sektör Seçimi */}
        <div className={asoStyles.filterPanel}>
          <div className={asoStyles.filterGroup}>
            <label>Sektör:</label>
            <select value={selectedSector} onChange={(e) => setSelectedSector(e.target.value)}>
              <option value="all">Tüm Sektörler</option>
              {sectors.map(sector => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sektörler Arası Karşılaştırma veya Tek Sektör Analizi */}
        <div className={asoStyles.chartsSection}>
          {/* Sektörler Arası Su Ayak İzi Karşılaştırması veya Tek Sektör Detayı */}
          <div className={asoStyles.chartCard}>
            <h3 className={asoStyles.chartTitle}>
              {selectedSector === 'all' 
                ? 'Sektörler Arası Su Ayak İzi Karşılaştırması'
                : `${selectedSector} Sektörü - Su Ayak İzi Analizi`}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={selectedSector === 'all' 
                ? sectors.map(sector => {
                    const sectorCompanies = companies.filter(c => c.sector === sector);
                    const footprints = sectorCompanies.map(c => c.footprint);
                    const avg = footprints.reduce((sum, f) => sum + f, 0) / footprints.length;
                    const min = Math.min(...footprints);
                    const max = Math.max(...footprints);
                    
                    return {
                      sector,
                      ortalama: parseFloat(avg.toFixed(2)),
                      minimum: parseFloat(min.toFixed(2)),
                      maksimum: parseFloat(max.toFixed(2)),
                      işletmeSayısı: sectorCompanies.length
                    };
                  })
                : (() => {
                    const sectorCompanies = filteredCompanies;
                    const footprints = sectorCompanies.map(c => c.footprint);
                    const avg = footprints.reduce((sum, f) => sum + f, 0) / footprints.length;
                    const min = Math.min(...footprints);
                    const max = Math.max(...footprints);
                    
                    // İşletmeleri su ayak izine göre grupla (10'ar gruplar)
                    const sortedCompanies = [...sectorCompanies].sort((a, b) => b.footprint - a.footprint);
                    const grouped = [];
                    for (let i = 0; i < sortedCompanies.length; i += 10) {
                      const group = sortedCompanies.slice(i, i + 10);
                      const groupFootprints = group.map(c => c.footprint);
                      const groupAvg = groupFootprints.reduce((sum, f) => sum + f, 0) / groupFootprints.length;
                      const groupMin = Math.min(...groupFootprints);
                      const groupMax = Math.max(...groupFootprints);
                      
                      grouped.push({
                        sector: `Grup ${Math.floor(i / 10) + 1}`,
                        ortalama: parseFloat(groupAvg.toFixed(2)),
                        minimum: parseFloat(groupMin.toFixed(2)),
                        maksimum: parseFloat(groupMax.toFixed(2)),
                        işletmeSayısı: group.length
                      });
                    }
                    
                    return grouped.length > 0 ? grouped : [{
                      sector: selectedSector,
                      ortalama: parseFloat(avg.toFixed(2)),
                      minimum: parseFloat(min.toFixed(2)),
                      maksimum: parseFloat(max.toFixed(2)),
                      işletmeSayısı: sectorCompanies.length
                    }];
                  })()}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.3)" />
                <XAxis 
                  dataKey="sector" 
                  tick={{ fill: '#1e293b', fontSize: 12 }}
                  angle={selectedSector !== 'all' ? -45 : 0}
                  textAnchor={selectedSector !== 'all' ? 'end' : 'middle'}
                  height={selectedSector !== 'all' ? 80 : 30}
                />
                <YAxis 
                  label={{ value: 'Su Ayak İzi (L/birim)', angle: -90, position: 'insideLeft' }}
                  tick={{ fill: '#1e293b', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                    border: '1px solid rgba(148, 163, 184, 0.5)',
                    color: '#1e293b',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                  labelStyle={{ color: '#1e293b', fontWeight: '600' }}
                  itemStyle={{ color: '#1e293b' }}
                />
                <Legend wrapperStyle={{ color: '#1e293b' }} />
                <Bar dataKey="ortalama" fill="#3b82f6" name="Ortalama" radius={[8, 8, 0, 0]} />
                <Bar dataKey="minimum" fill="#3b82f6" name="Minimum" radius={[8, 8, 0, 0]} opacity={0.6} />
                <Bar dataKey="maksimum" fill="#3b82f6" name="Maksimum" radius={[8, 8, 0, 0]} opacity={0.8} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Sektörler Arası Detaylı İstatistikler veya Tek Sektör İstatistikleri */}
          {selectedSector === 'all' ? (
            <div className={asoStyles.chartCard}>
              <h3 className={asoStyles.chartTitle}>Sektörler Arası Detaylı İstatistikler</h3>
              <div className={asoStyles.tableWrapper}>
                <table className={asoStyles.companiesTable}>
                  <thead>
                    <tr>
                      <th>Sektör</th>
                      <th>İşletme Sayısı</th>
                      <th>Ortalama (L/birim)</th>
                      <th>Minimum (L/birim)</th>
                      <th>Maksimum (L/birim)</th>
                      <th>Toplam Üretim</th>
                      <th>Toplam Su (kL)</th>
                      <th>Ort. Geri Kazanım (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectors.map(sector => {
                      const sectorCompanies = companies.filter(c => c.sector === sector);
                      const footprints = sectorCompanies.map(c => c.footprint);
                      const avg = footprints.reduce((sum, f) => sum + f, 0) / footprints.length;
                      const min = Math.min(...footprints);
                      const max = Math.max(...footprints);
                      const totalProduction = sectorCompanies.reduce((sum, c) => sum + c.production, 0);
                      const totalWater = sectorCompanies.reduce((sum, c) => sum + c.totalWater, 0);
                      const avgReuse = sectorCompanies.reduce((sum, c) => sum + c.reusePotential, 0) / sectorCompanies.length;
                      
                      return (
                        <tr key={sector}>
                          <td><strong>{sector}</strong></td>
                          <td>{sectorCompanies.length}</td>
                          <td>{avg.toFixed(2)}</td>
                          <td>{min.toFixed(2)}</td>
                          <td>{max.toFixed(2)}</td>
                          <td>{totalProduction.toLocaleString('tr-TR')}</td>
                          <td>{(totalWater / 1000).toFixed(1)}k</td>
                          <td>
                            <span className={asoStyles.statusBadge} style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }}>
                              %{avgReuse.toFixed(1)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className={asoStyles.chartCard}>
              <h3 className={asoStyles.chartTitle}>{selectedSector} Sektörü - Özet İstatistikler</h3>
              <div className={asoStyles.tableWrapper}>
                <table className={asoStyles.companiesTable}>
                  <thead>
                    <tr>
                      <th>Metrik</th>
                      <th>Değer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const sectorCompanies = filteredCompanies;
                      const footprints = sectorCompanies.map(c => c.footprint);
                      const avg = footprints.reduce((sum, f) => sum + f, 0) / footprints.length;
                      const min = Math.min(...footprints);
                      const max = Math.max(...footprints);
                      const totalProduction = sectorCompanies.reduce((sum, c) => sum + c.production, 0);
                      const totalWater = sectorCompanies.reduce((sum, c) => sum + c.totalWater, 0);
                      const avgReuse = sectorCompanies.reduce((sum, c) => sum + c.reusePotential, 0) / sectorCompanies.length;
                      
                      return [
                        { metric: 'İşletme Sayısı', value: sectorCompanies.length },
                        { metric: 'Ortalama Su Ayak İzi', value: `${avg.toFixed(2)} L/birim` },
                        { metric: 'Minimum Su Ayak İzi', value: `${min.toFixed(2)} L/birim` },
                        { metric: 'Maksimum Su Ayak İzi', value: `${max.toFixed(2)} L/birim` },
                        { metric: 'Toplam Üretim', value: totalProduction.toLocaleString('tr-TR') + ' birim' },
                        { metric: 'Toplam Su Tüketimi', value: `${(totalWater / 1000).toFixed(1)} kL` },
                        { metric: 'Ortalama Geri Kazanım Potansiyeli', value: `%${avgReuse.toFixed(1)}` }
                      ];
                    })().map((row, idx) => (
                      <tr key={idx}>
                        <td><strong>{row.metric}</strong></td>
                        <td>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Seçilen Sektör Detay Analizi */}
        {selectedSector !== 'all' && (
          <div className={asoStyles.companiesTableSection}>
            <h3 className={asoStyles.sectionTitle}>
              {selectedSector} Sektörü - Detaylı İşletme Analizi ({filteredCompanies.length} işletme)
            </h3>
            <div className={asoStyles.tableWrapper}>
              <table className={asoStyles.companiesTable}>
                <thead>
                  <tr>
                    <th>İşletme Adı</th>
                    <th>Üretim (birim)</th>
                    <th>Toplam Su (L)</th>
                    <th>Su Ayak İzi (L/birim)</th>
                    <th>Geri Kazanım Potansiyeli (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.length > 0 ? (
                    filteredCompanies
                      .sort((a, b) => b.footprint - a.footprint)
                      .slice(0, 50)
                      .map(company => (
                        <tr key={company.id}>
                          <td><strong>{company.name}</strong></td>
                          <td>{company.production.toLocaleString('tr-TR')}</td>
                          <td>{(company.totalWater / 1000).toFixed(1)}k</td>
                          <td>{company.footprint.toFixed(2)}</td>
                          <td>
                            <span className={asoStyles.statusBadge} style={{ background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }}>
                              %{company.reusePotential}
                            </span>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                        Seçilen sektör için veri bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// İş Paketi 6: AI Analizler Dashboard
const AIAnalyticsDashboard = () => {
  const [efficiencyData] = useState(() => {
    const sectors = ['Tekstil', 'Gıda', 'Plastik', 'Metal', 'Kimya'];
    return sectors.map(sector => ({
      sector,
      efficiency: Math.floor(Math.random() * 30) + 70, // %70-100
      risk: ['Düşük', 'Orta', 'Yüksek'][Math.floor(Math.random() * 3)],
      anomalies: Math.floor(Math.random() * 10),
      prediction: Math.floor(Math.random() * 20) + 80
    }));
  });

  const anomalyPredictions = [
    { company: 'ABC Tekstil', type: 'Su Tüketimi Anomalisi', probability: 85, severity: 'Yüksek', recommendation: 'Anında müdahale gerekli' },
    { company: 'XYZ Gıda', type: 'Atık Su Yükü Anomalisi', probability: 72, severity: 'Orta', recommendation: 'İzleme ve kontrol' },
    { company: 'DEF Plastik', type: 'Verimlilik Düşüşü', probability: 68, severity: 'Orta', recommendation: 'Proses optimizasyonu' }
  ];

  return (
    <div className={asoStyles.dashboardContent}>
      <div className={asoStyles.companyDashboard}>
        <h2 className={asoStyles.sectionTitle}>Yapay Zekâ Tabanlı Analizler ve Karar Destek Sistemi</h2>
        <p className={asoStyles.companyDescription}>
          Anormallik tespiti, öngörü ve optimizasyon sağlayan yapay zekâ modelleri
        </p>

        {/* Verimlilik Analizleri */}
        <div className={asoStyles.chartCard}>
          <h3 className={asoStyles.chartTitle}>Sektörel Verimlilik Analizleri</h3>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={efficiencyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.3)" />
              <XAxis 
                dataKey="sector" 
                tick={{ fill: '#1e293b', fontSize: 12 }}
              />
              <YAxis 
                yAxisId="left" 
                label={{ value: 'Verimlilik (%)', angle: -90, position: 'insideLeft' }}
                tick={{ fill: '#1e293b', fontSize: 12 }}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                label={{ value: 'Anomali Sayısı', angle: 90, position: 'insideRight' }}
                tick={{ fill: '#1e293b', fontSize: 12 }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                  border: '1px solid rgba(148, 163, 184, 0.5)',
                  color: '#1e293b',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                labelStyle={{ color: '#1e293b', fontWeight: '600' }}
                itemStyle={{ color: '#1e293b' }}
              />
              <Legend 
                wrapperStyle={{ color: '#1e293b' }}
              />
              <Bar yAxisId="left" dataKey="efficiency" fill="#10b981" name="Verimlilik (%)" radius={[8, 8, 0, 0]} />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="anomalies" 
                stroke="#ef4444" 
                strokeWidth={2} 
                name="Anomali Sayısı"
                dot={{ fill: '#ef4444', r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Anomali Öngörüleri */}
        <div className={asoStyles.chartCard}>
          <h3 className={asoStyles.chartTitle}>AI Tabanlı Anomali Öngörüleri</h3>
          <div className={asoStyles.tableWrapper}>
            <table className={asoStyles.companiesTable}>
              <thead>
                <tr>
                  <th>İşletme</th>
                  <th>Anomali Tipi</th>
                  <th>Olasılık (%)</th>
                  <th>Şiddet</th>
                  <th>Öneri</th>
                </tr>
              </thead>
              <tbody>
                {anomalyPredictions.map((pred, idx) => (
                  <tr key={idx}>
                    <td><strong>{pred.company}</strong></td>
                    <td>{pred.type}</td>
                    <td>
                      <span className={asoStyles.statusBadge} style={{ 
                        background: pred.probability > 75 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                        color: pred.probability > 75 ? '#ef4444' : '#f59e0b'
                      }}>
                        %{pred.probability}
                      </span>
                    </td>
                    <td>
                      <span className={`${asoStyles.statusBadge} ${pred.severity === 'Yüksek' ? asoStyles.statusWarning : asoStyles.statusActive}`}>
                        {pred.severity}
                      </span>
                    </td>
                    <td>{pred.recommendation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// İş Paketi 7: Raporlama Dashboard
const ReportingDashboard = () => {
  const [reports] = useState([
    { id: 1, name: 'OSB Su Yönetimi Özet Raporu', type: 'Özet', date: '2025-01-15', status: 'Hazır' },
    { id: 2, name: 'Sektör Bazlı Su Tüketim Analizi', type: 'Analiz', date: '2025-01-14', status: 'Hazır' },
    { id: 3, name: 'Kuraklık Senaryosu Raporu', type: 'Senaryo', date: '2025-01-13', status: 'Hazır' },
    { id: 4, name: 'Atık Su ve Arıtma Fizibilite Raporu', type: 'Fizibilite', date: '2025-01-12', status: 'Hazır' },
    { id: 5, name: 'Su Ayak İzi Analiz Raporu', type: 'Analiz', date: '2025-01-11', status: 'Hazır' },
    { id: 6, name: 'AI Analiz ve Karar Destek Raporu', type: 'AI', date: '2025-01-10', status: 'Hazır' }
  ]);

  const exportReport = (reportId) => {
    const report = reports.find(r => r.id === reportId);
    alert(`${report.name} raporu indiriliyor...`);
  };

  return (
    <div className={asoStyles.dashboardContent}>
      <div className={asoStyles.companyDashboard}>
        <h2 className={asoStyles.sectionTitle}>Raporlama ve Yönetim Paneli</h2>
        <p className={asoStyles.companyDescription}>
          OSB yönetimi ve işletmeler için kapsamlı raporlama ve karar destek çıktıları
        </p>

        {/* Rapor Listesi */}
        <div className={asoStyles.reportsGrid}>
          {reports.map(report => (
            <div key={report.id} className={asoStyles.reportCard}>
              <div className={asoStyles.reportHeader}>
                <h3>{report.name}</h3>
                <span className={`${asoStyles.statusBadge} ${asoStyles.statusActive}`}>
                  {report.status}
                </span>
              </div>
              <div className={asoStyles.reportMeta}>
                <span>Tip: {report.type}</span>
                <span>Tarih: {report.date}</span>
              </div>
              <div className={asoStyles.reportActions}>
                <button 
                  className={asoStyles.excelButton}
                  onClick={() => exportReport(report.id)}
                >
                  <Download size={16} />
                  PDF İndir
                </button>
                <button 
                  className={asoStyles.filterButton}
                  onClick={() => exportReport(report.id)}
                >
                  Excel İndir
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Company Dashboard Component
const CompanyDashboard = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [showMap, setShowMap] = useState(false);
  const [selectedSector, setSelectedSector] = useState('all');
  const [selectedArea, setSelectedArea] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Mock company data with location, revenue, and water consumption
  const [companies] = useState(() => {
    const sectors = ['Tekstil', 'Gıda', 'Plastik', 'Metal', 'Kimya', 'Otomotiv', 'Kağıt', 'Elektronik'];
    const areas = ['A Bölgesi', 'B Bölgesi', 'C Bölgesi', 'D Bölgesi', 'E Bölgesi'];
    const companies = [];
    
    // Sanayi bölgesi merkez koordinatları (Ankara örneği)
    const baseLat = 39.9334;
    const baseLng = 32.8597;
    
    // Sektör bazlı ortalama ciro ve su tüketimi değerleri (daha gerçekçi)
    const sectorBaselines = {
      'Tekstil': { revenue: [50, 200], water: [500, 2000] }, // 50-200M TL, 500k-2M L
      'Gıda': { revenue: [80, 300], water: [800, 3000] },
      'Plastik': { revenue: [30, 150], water: [300, 1500] },
      'Metal': { revenue: [100, 400], water: [1000, 4000] },
      'Kimya': { revenue: [150, 500], water: [1500, 5000] },
      'Otomotiv': { revenue: [200, 800], water: [2000, 8000] },
      'Kağıt': { revenue: [40, 180], water: [400, 1800] },
      'Elektronik': { revenue: [60, 250], water: [600, 2500] }
    };
    
    for (let i = 1; i <= 350; i++) {
      const sector = sectors[Math.floor(Math.random() * sectors.length)];
      const area = areas[Math.floor(Math.random() * areas.length)];
      const baseline = sectorBaselines[sector];
      
      // Daha gerçekçi değerler (milyon TL ve bin L cinsinden)
      const revenue = (Math.random() * (baseline.revenue[1] - baseline.revenue[0]) + baseline.revenue[0]) * 1000000; // Milyon TL
      const waterConsumption = (Math.random() * (baseline.water[1] - baseline.water[0]) + baseline.water[0]) * 1000; // Bin L
      
      const lat = baseLat + (Math.random() - 0.5) * 0.05; // ±0.025 derece
      const lng = baseLng + (Math.random() - 0.5) * 0.05;
      
      companies.push({
        id: i,
        name: `${sector} Şirketi ${i}`,
        sector,
        area,
        revenue,
        waterConsumption,
        electricity: Math.floor(Math.random() * 50000) + 5000,
        naturalGas: Math.floor(Math.random() * 30000) + 3000,
        lat,
        lng,
        status: Math.random() > 0.1 ? 'active' : 'warning',
        employees: Math.floor(Math.random() * 200) + 10
      });
    }
    return companies;
  });

  // Filtered companies
  const filteredCompanies = companies.filter(company => {
    if (selectedSector !== 'all' && company.sector !== selectedSector) return false;
    if (selectedArea !== 'all' && company.area !== selectedArea) return false;
    return true;
  });

  // Heatmap data for water consumption
  const heatmapData = filteredCompanies.map(company => ({
    lat: company.lat,
    lng: company.lng,
    value: company.waterConsumption,
    name: company.name
  }));

  // Chart data - Revenue vs Water Consumption (filtrelenmiş şirketlere göre)
  const chartData = filteredCompanies
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 20)
    .map(company => ({
      name: company.name, // Tam isim göster
      revenue: company.revenue / 1000000, // Milyon TL
      waterConsumption: company.waterConsumption / 1000, // Bin L
      efficiency: (company.revenue / company.waterConsumption).toFixed(2)
    }));

  // Area-based statistics
  const areaStats = filteredCompanies.reduce((acc, company) => {
    if (!acc[company.area]) {
      acc[company.area] = {
        area: company.area,
        companies: 0,
        totalRevenue: 0,
        totalWaterConsumption: 0
      };
    }
    acc[company.area].companies++;
    acc[company.area].totalRevenue += company.revenue;
    acc[company.area].totalWaterConsumption += company.waterConsumption;
    return acc;
  }, {});

  const areaChartData = Object.values(areaStats).map(stat => ({
    area: stat.area,
    revenue: stat.totalRevenue / 1000000,
    waterConsumption: stat.totalWaterConsumption / 1000,
    companies: stat.companies
  }));

  // Initialize map
  useEffect(() => {
    if (!showMap || !mapContainer.current || map.current) return;

    const maplibregl = window.maplibregl;
    if (!maplibregl) {
      console.error('MapLibre GL not loaded');
      return;
    }

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: 'https://api.maptiler.com/maps/basic-v2/style.json?key=' + mapTilerKey,
      center: [32.8597, 39.9334], // Ankara
      zoom: 12,
      pitch: 45,
      bearing: -17.6
    });

    map.current.on('load', () => {
      // Add heatmap layer
      const heatmapSource = {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: heatmapData.map(point => ({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [point.lng, point.lat]
            },
            properties: {
              value: point.value
            }
          }))
        }
      };

      map.current.addSource('heatmap', heatmapSource);

      // Add heatmap layer
      map.current.addLayer({
        id: 'heatmap-layer',
        type: 'heatmap',
        source: 'heatmap',
        maxzoom: 15,
        paint: {
          'heatmap-weight': {
            property: 'value',
            type: 'exponential',
            stops: [[0, 0], [1, 1]]
          },
          'heatmap-intensity': {
            stops: [[0, 0], [15, 1]]
          },
          'heatmap-color': [
            'interpolate',
            ['linear'],
            ['heatmap-density'],
            0, 'rgba(33,102,172,0)',
            0.2, 'rgb(103,169,207)',
            0.4, 'rgb(209,229,240)',
            0.6, 'rgb(253,219,199)',
            0.8, 'rgb(239,138,98)',
            1, 'rgb(178,24,43)'
          ],
          'heatmap-radius': {
            stops: [[0, 2], [15, 20]]
          },
          'heatmap-opacity': 0.6
        }
      });

      // Add points layer
      map.current.addLayer({
        id: 'points-layer',
        type: 'circle',
        source: 'heatmap',
        paint: {
          'circle-radius': 8,
          'circle-color': [
            'interpolate',
            ['linear'],
            ['get', 'value'],
            0, '#3b82f6',
            50000, '#10b981',
            100000, '#f59e0b',
            150000, '#ef4444'
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff'
        }
      });

      // Add popup on click
      map.current.on('click', 'points-layer', (e) => {
        const feature = e.features[0];
        const company = filteredCompanies.find(c => 
          Math.abs(c.lat - feature.geometry.coordinates[1]) < 0.001 &&
          Math.abs(c.lng - feature.geometry.coordinates[0]) < 0.001
        );
        
        if (company) {
          new maplibregl.Popup()
            .setLngLat(e.lngLat)
            .setHTML(`
              <div style="padding: 8px;">
                <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">${company.name}</h3>
                <p style="margin: 4px 0; font-size: 12px;"><strong>Sektör:</strong> ${company.sector}</p>
                <p style="margin: 4px 0; font-size: 12px;"><strong>Bölge:</strong> ${company.area}</p>
                <p style="margin: 4px 0; font-size: 12px;"><strong>Su Tüketimi:</strong> ${(company.waterConsumption / 1000).toFixed(1)}k L</p>
                <p style="margin: 4px 0; font-size: 12px;"><strong>Ciro:</strong> ${(company.revenue / 1000000).toFixed(1)}M TL</p>
              </div>
            `)
            .addTo(map.current);
        }
      });
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [showMap, heatmapData, filteredCompanies]);

  // Update map when filters change
  useEffect(() => {
    if (!map.current || !map.current.getSource('heatmap')) return;

    const newHeatmapData = {
      type: 'FeatureCollection',
      features: heatmapData.map(point => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [point.lng, point.lat]
        },
        properties: {
          value: point.value
        }
      }))
    };

    map.current.getSource('heatmap').setData(newHeatmapData);
  }, [heatmapData]);

  // Export to Excel
  const exportToExcel = () => {
    const data = filteredCompanies.map(company => ({
      'Şirket Adı': company.name,
      'Sektör': company.sector,
      'Bölge': company.area,
      'Ciro (TL)': company.revenue,
      'Su Tüketimi (L)': company.waterConsumption,
      'Elektrik (kWh)': company.electricity,
      'Doğal Gaz (m³)': company.naturalGas,
      'Çalışan Sayısı': company.employees,
      'Durum': company.status === 'active' ? 'Normal' : 'Anomali'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Şirketler');
    XLSX.writeFile(wb, 'sirketler_raporu.xlsx');
  };

  const sectors = [...new Set(companies.map(c => c.sector))];
  const areas = [...new Set(companies.map(c => c.area))];

  return (
    <div className={asoStyles.dashboardContent}>
      <div className={asoStyles.companyDashboard}>
        <div className={asoStyles.companyHeaderSection}>
          <div>
            <h2 className={asoStyles.sectionTitle}>Fabrika ve Şirketler Dashboard</h2>
            <p className={asoStyles.companyDescription}>
              {filteredCompanies.length} şirket görüntüleniyor. Su tüketimi, ciro ve bölge bazlı analizler.
            </p>
          </div>
          <div className={asoStyles.companyActions}>
            <button
              className={asoStyles.filterButton}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={18} />
              Filtrele
            </button>
            <button
              className={asoStyles.excelButton}
              onClick={exportToExcel}
            >
              <Download size={18} />
              Excel İndir
            </button>
            <button
              className={`${asoStyles.mapButton} ${showMap ? asoStyles.active : ''}`}
              onClick={() => setShowMap(!showMap)}
            >
              <MapIcon size={18} />
              {showMap ? 'Haritayı Kapat' : 'Haritayı Aç'}
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className={asoStyles.filterPanel}>
            <div className={asoStyles.filterHeader}>
              <h3>Filtreleme</h3>
              <button onClick={() => setShowFilters(false)}>
                <X size={18} />
              </button>
            </div>
            <div className={asoStyles.filterContent}>
              <div className={asoStyles.filterGroup}>
                <label>Sektör:</label>
                <select
                  value={selectedSector}
                  onChange={(e) => setSelectedSector(e.target.value)}
                >
                  <option value="all">Tümü</option>
                  {sectors.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
                          </div>
              <div className={asoStyles.filterGroup}>
                <label>Bölge:</label>
                <select
                  value={selectedArea}
                  onChange={(e) => setSelectedArea(e.target.value)}
                >
                  <option value="all">Tümü</option>
                  {areas.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
                          </div>
                        </div>
                      </div>
        )}

        {/* Map */}
        {showMap && (
          <div className={asoStyles.mapContainer}>
            <div ref={mapContainer} className={asoStyles.map} />
            <div className={asoStyles.mapLegend}>
              <h4>Su Tüketimi Heatmap</h4>
              <div className={asoStyles.legendItems}>
                <div className={asoStyles.legendItem}>
                  <div className={asoStyles.legendColor} style={{ background: '#3b82f6' }}></div>
                  <span>Düşük (&lt;50k L)</span>
                </div>
                <div className={asoStyles.legendItem}>
                  <div className={asoStyles.legendColor} style={{ background: '#10b981' }}></div>
                  <span>Orta (50k-100k L)</span>
                </div>
                <div className={asoStyles.legendItem}>
                  <div className={asoStyles.legendColor} style={{ background: '#f59e0b' }}></div>
                  <span>Yüksek (100k-150k L)</span>
                </div>
                <div className={asoStyles.legendItem}>
                  <div className={asoStyles.legendColor} style={{ background: '#ef4444' }}></div>
                  <span>Çok Yüksek (&gt;150k L)</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts Section */}
        <div className={asoStyles.chartsSection}>
          {/* Revenue vs Water Consumption Scatter */}
          <div className={asoStyles.chartCard}>
            <h3 className={asoStyles.chartTitle}>
              {selectedSector === 'all' 
                ? 'Ciro vs Su Tüketimi (Top 20 Şirket)' 
                : `${selectedSector} Sektörü - Ciro vs Su Tüketimi (Top 20 Şirket)`}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="revenue" 
                  name="Ciro" 
                  unit="M TL"
                  label={{ value: 'Ciro (Milyon TL)', position: 'insideBottom', offset: -5 }}
                  tick={{ fill: '#1e293b', fontSize: 12 }}
                />
                <YAxis 
                  dataKey="waterConsumption" 
                  name="Su Tüketimi" 
                  unit="k L"
                  label={{ value: 'Su Tüketimi (Bin L)', angle: -90, position: 'insideLeft' }}
                  tick={{ fill: '#1e293b', fontSize: 12 }}
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  formatter={(value, name) => {
                    if (name === 'revenue') return `${value.toFixed(1)} M TL`;
                    if (name === 'waterConsumption') return `${value.toFixed(1)} k L`;
                    return value;
                  }}
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                    border: '1px solid rgba(148, 163, 184, 0.5)',
                    color: '#1e293b',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                  labelStyle={{ color: '#1e293b', fontWeight: '600' }}
                  itemStyle={{ color: '#1e293b' }}
                />
                <Legend wrapperStyle={{ color: '#1e293b' }} />
                <Scatter name="Şirketler" dataKey="waterConsumption" fill="#3b82f6">
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill="#3b82f6" />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* Area-based Revenue and Water Consumption */}
          <div className={asoStyles.chartCard}>
            <h3 className={asoStyles.chartTitle}>
              {selectedSector === 'all' 
                ? 'Bölge Bazlı Ciro ve Su Tüketimi' 
                : `${selectedSector} Sektörü - Bölge Bazlı Ciro ve Su Tüketimi`}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={areaChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="area" 
                  tick={{ fill: '#1e293b', fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="left" 
                  label={{ value: 'Ciro (Milyon TL)', angle: -90, position: 'insideLeft' }}
                  tick={{ fill: '#1e293b', fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  label={{ value: 'Su Tüketimi (Bin L)', angle: 90, position: 'insideRight' }}
                  tick={{ fill: '#1e293b', fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'revenue') return `${value.toFixed(1)} M TL`;
                    if (name === 'waterConsumption') return `${value.toFixed(1)} k L`;
                    return value;
                  }}
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                    border: '1px solid rgba(148, 163, 184, 0.5)',
                    color: '#1e293b',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                  labelStyle={{ color: '#1e293b', fontWeight: '600' }}
                  itemStyle={{ color: '#1e293b' }}
                />
                <Legend wrapperStyle={{ color: '#1e293b' }} />
                <Bar yAxisId="left" dataKey="revenue" fill="#3b82f6" name="Ciro (M TL)" />
                <Line yAxisId="right" type="monotone" dataKey="waterConsumption" stroke="#10b981" strokeWidth={2} name="Su Tüketimi (k L)" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Top Companies by Revenue */}
          <div className={asoStyles.chartCard}>
            <h3 className={asoStyles.chartTitle}>
              {selectedSector === 'all' 
                ? 'En Yüksek Ciroya Sahip Şirketler (Top 15)' 
                : `${selectedSector} Sektörü - En Yüksek Ciroya Sahip Şirketler (Top 15)`}
            </h3>
            <ResponsiveContainer width="100%" height={500}>
              <BarChart data={chartData.slice(0, 15)} margin={{ bottom: 120, left: 20, right: 20, top: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={150}
                  interval={0}
                  tick={{ fontSize: 11, fill: '#1e293b' }}
                />
                <YAxis 
                  label={{ value: 'Ciro (Milyon TL)', angle: -90, position: 'insideLeft' }}
                  domain={[0, 'dataMax + 50']}
                  tick={{ fill: '#1e293b', fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value) => [`${value.toFixed(2)} M TL`, 'Ciro']}
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                    border: '1px solid rgba(148, 163, 184, 0.5)',
                    color: '#1e293b',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                  labelStyle={{ color: '#1e293b', fontWeight: '600', marginBottom: 8 }}
                  itemStyle={{ color: '#1e293b' }}
                />
                <Legend wrapperStyle={{ color: '#1e293b' }} />
                <Bar dataKey="revenue" fill="#3b82f6" name="Ciro (Milyon TL)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top Companies by Water Consumption */}
          <div className={asoStyles.chartCard}>
            <h3 className={asoStyles.chartTitle}>
              {selectedSector === 'all' 
                ? 'En Yüksek Su Tüketimine Sahip Şirketler (Top 15)' 
                : `${selectedSector} Sektörü - En Yüksek Su Tüketimine Sahip Şirketler (Top 15)`}
            </h3>
            <ResponsiveContainer width="100%" height={500}>
              <BarChart 
                data={[...filteredCompanies]
                  .sort((a, b) => b.waterConsumption - a.waterConsumption)
                  .slice(0, 15)
                  .map(c => ({
                    name: c.name, // Tam isim göster
                    waterConsumption: c.waterConsumption / 1000 // Bin L
                  }))}
                margin={{ bottom: 120, left: 20, right: 20, top: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={150}
                  interval={0}
                  tick={{ fontSize: 11, fill: '#1e293b' }}
                />
                <YAxis 
                  label={{ value: 'Su Tüketimi (Bin L)', angle: -90, position: 'insideLeft' }}
                  domain={[0, 'dataMax + 500']}
                  tick={{ fill: '#1e293b', fontSize: 12 }}
                />
                <Tooltip 
                  formatter={(value) => [`${value.toFixed(1)} k L`, 'Su Tüketimi']}
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.98)', 
                    border: '1px solid rgba(148, 163, 184, 0.5)',
                    color: '#1e293b',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                  labelStyle={{ color: '#1e293b', fontWeight: '600', marginBottom: 8 }}
                  itemStyle={{ color: '#1e293b' }}
                />
                <Legend wrapperStyle={{ color: '#1e293b' }} />
                <Bar dataKey="waterConsumption" fill="#10b981" name="Su Tüketimi (Bin L)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Companies Table */}
        <div className={asoStyles.companiesTableSection}>
          <h3 className={asoStyles.sectionTitle}>Şirket Listesi</h3>
          <div className={asoStyles.tableWrapper}>
            <table className={asoStyles.companiesTable}>
              <thead>
                <tr>
                  <th>Şirket Adı</th>
                  <th>Sektör</th>
                  <th>Bölge</th>
                  <th>Ciro (TL)</th>
                  <th>Su Tüketimi (L)</th>
                  <th>Elektrik (kWh)</th>
                  <th>Doğal Gaz (m³)</th>
                  <th>Çalışan</th>
                  <th>Durum</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompanies.slice(0, 50).map(company => (
                  <tr key={company.id}>
                    <td><strong>{company.name}</strong></td>
                    <td>{company.sector}</td>
                    <td>{company.area}</td>
                    <td>{(company.revenue / 1000000).toFixed(2)}M</td>
                    <td>{(company.waterConsumption / 1000).toFixed(1)}k</td>
                    <td>{(company.electricity / 1000).toFixed(1)}k</td>
                    <td>{(company.naturalGas / 1000).toFixed(1)}k</td>
                    <td>{company.employees}</td>
                    <td>
                      <span className={`${asoStyles.statusBadge} ${company.status === 'active' ? asoStyles.statusActive : asoStyles.statusWarning}`}>
                        {company.status === 'active' ? 'Normal' : 'Anomali'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryDashboard;
